import { FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import {
  Panel,
  PanelHeader,
  Button,
  Spinner,
  Container,
  Image,
} from "components";
import { Col, ElementWrapper, Row } from "components/BaseComponents/Flex";
import {
  AdminSecurityPhotoListAsync,
  AdminSelectedSecurityPhotoAsync,
} from "services/password/password.service";
import { TokenInclude } from "helpers/GetToken";
import Alert from "components/BaseComponents/Alert/Alert";
import { useAppDispatch } from "redux/hooks";
import {
  setFirstPassChangeAdmin,
  setFirstPassChangeAdminGuv,
} from "redux/slices/password-slices/PasswordRefreshSlice";
import Success from "./Success";

const AdminSecurityPhotoChange: FC<{}> = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const token = TokenInclude();

  const [isLoading, setIsLoading] = useState(false);
  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(
    t("success_security_photo").toString()
  );

  const [secPhotos, setSecPhoto] = useState<any[]>([]);
  const [selectedPic, setSelectedPic] = useState<string>("");
  const [confirmPhoto, setConfirmPhoto] = useState<boolean>(false);

  const selectedPhotoConfirm = async (sPhotoId: string): Promise<void> => {
    const retVal = await AdminSelectedSecurityPhotoAsync(token, sPhotoId);

    if (retVal?.DialogBoxes?.length < 1) {
      // dispatch(setFirstPassChangeAdmin(true));
      dispatch(setFirstPassChangeAdminGuv(true));
      setConfirmPhoto(true);
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
      return;
    }
  };

  const onSelectPhoto = (p: any) => {
    setSelectedPic(p.Key);
  };

  useEffect(() => {
    async function getSecPhotoList() {
      setIsLoading(true);
      const secPhotoList = await AdminSecurityPhotoListAsync(token);
      if (secPhotoList?.DialogBoxes?.length < 1) {
        setIsLoading(false);
        setSecPhoto(secPhotoList.SecPics);
      } else {
        const errMessage: any = secPhotoList;
        setIsAddError(true);
        setTxt(errMessage);
        setIsLoading(false);
        return;
      }
    }
    if (secPhotos.length < 1) {
      getSecPhotoList();
    }
  }, []);

  return (
    <Container>
      <ElementWrapper
        minHeight={isMobile ? "calc(100vh - 118px)" : "calc(100vh - 137px)"}
        padding='48px 0 0 0'
        width='872px'
      >
        {confirmPhoto ? (
          <Panel width='646px' marginTop='0'>
            <>
              <PanelHeader
                backgroundColor={DIGIPOSTHEME.colors.transparent}
                title={t("password_refresh_done")}
              />
              <Success
                buttonText={t("ok").toString()}
                onClick={() => navigate("/choice-merchant")}
                noPadding
              />
            </>
          </Panel>
        ) : (
          <Panel width='646px' marginTop='0'>
            {(isAddSuccess || isAddError) && (
              <Alert
                alertType={isAddSuccess ? "Success" : "Error"}
                duration={6000}
                isVisible={isAddSuccess || isAddError}
                message={txt}
                setIsAddSuccess={setIsAddSuccess}
                setIsAddError={setIsAddError}
              />
            )}
            <Spinner show={isLoading} />
            <PanelHeader title={t("password_security_picture_select")} />
            <Row
              backgroundColor={DIGIPOSTHEME.colors.secondaryWhite}
              justifyContent='center'
            >
              {secPhotos.map((p) => (
                <Image
                  key={p.Key}
                  alt='Admin Security Photo'
                  srcSet={
                    p.Key ? `data:image/png;base64, ${p.Value}` : undefined
                  }
                  height='130px'
                  width='130px'
                  borderRadius='10px'
                  border={`2px solid ${
                    p.Key === selectedPic
                      ? DIGIPOSTHEME.colors.headerRed
                      : "#f5f4f6"
                  } `}
                  margin={isMobile ? "35px" : "20px"}
                  padding='5px'
                  backgroundColor={DIGIPOSTHEME.colors.footerGrey}
                  opacity={!selectedPic ? 1 : p.Key !== selectedPic ? 0.5 : 1}
                  onClick={() => onSelectPhoto(p)}
                />
              ))}
            </Row>

            <Row
              backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
              justifyContent='flex-end'
              isResponsive
              noMargin
              padding='20px 0 10px 0'
              gap
            >
              <Col offSet={22} size={0.5}>
                <Button
                  text={t("confirm").toString()}
                  type='button'
                  onClick={() => selectedPhotoConfirm(selectedPic)}
                  disabled={!selectedPic}
                />
              </Col>
            </Row>
          </Panel>
        )}
      </ElementWrapper>
    </Container>
  );
};

export default AdminSecurityPhotoChange;
